import { useQueryClient } from '@tanstack/react-query';
import { getAddress } from 'viem';
import { useConnection, useSignMessage } from 'wagmi';

import { QueryKeys } from '@/app/shared/constants/queryKeys';
import { buildReferralSignMessage, toReferralApiCode } from '@/app/shared/lib/referralSigning';
import {
  bindReferralCode,
  createReferralCode,
  ReferralRequestError,
} from '@/app/shared/services/api';
import {
  CreateReferralCodeResponse,
  UseReferralCodeResponse,
} from '@/app/shared/services/api/types';

const mapReferralError = (error: unknown): string => {
  if (error instanceof ReferralRequestError) {
    if (error.status === 401) return error.message || 'Signature expired. Please try again.';
    return error.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong. Please try again.';
};

export const useReferralActions = () => {
  const { address } = useConnection();
  const { mutateAsync: signMessage } = useSignMessage();
  const queryClient = useQueryClient();

  const invalidateReferralQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [QueryKeys.REFERRAL_SUMMARY] }),
      queryClient.invalidateQueries({ queryKey: [QueryKeys.REFERRAL_INVITEES] }),
    ]);
  };

  const signAndSubmit = async (
    action: 'create-referral-code' | 'use-referral-code',
    code: string,
  ) => {
    if (!address) throw new Error('Wallet not connected');

    const checksummed = getAddress(address);
    const apiCode = toReferralApiCode(code);
    const issuedAt = new Date().toISOString();
    const message = buildReferralSignMessage(action, checksummed, apiCode, issuedAt);
    const signature = await signMessage({ message });

    const body = {
      address: checksummed,
      code: apiCode,
      issuedAt,
      signature,
    };

    if (action === 'create-referral-code') {
      return createReferralCode(body);
    }
    return bindReferralCode(body);
  };

  const createCode = async (code: string): Promise<CreateReferralCodeResponse> => {
    const result = (await signAndSubmit('create-referral-code', code)) as CreateReferralCodeResponse;
    await invalidateReferralQueries();
    return result;
  };

  const bindCode = async (code: string): Promise<UseReferralCodeResponse> => {
    const result = (await signAndSubmit('use-referral-code', code)) as UseReferralCodeResponse;
    await invalidateReferralQueries();
    return result;
  };

  return {
    createCode,
    bindCode,
    mapReferralError,
  };
};
